/**
 * Dev dispatch collection — typed CRUD on top of the generic store.
 *
 * A DevDispatch tracks one ticket handed to a coding agent through the
 * dev pipeline: proposed → dispatched → pr_open → merged (or failed).
 * The PURE state machine lives in `src/lib/dev/dispatchState.ts`; this
 * module is only persistence. Modelled on `tickets.ts` /
 * `deliverables.ts` — when we cut over to Firestore the only thing
 * that changes is the adapter selection in `store.ts`.
 */

import { db, nowIso, uuid } from './store';
import type { DevDispatch } from './types';

export async function listDevDispatch(): Promise<DevDispatch[]> {
  return db.list('devDispatch');
}

export async function getDevDispatch(id: string): Promise<DevDispatch | null> {
  return db.get('devDispatch', id);
}

export function subscribeDevDispatch(
  fn: (records: DevDispatch[]) => void,
): () => void {
  return db.subscribe('devDispatch', fn);
}

/** What a caller supplies to create a dispatch — we mint the rest. */
export interface NewDevDispatchInput {
  ticketId: string;
  ticketTitle: string;
  repo?: string;
  state?: DevDispatch['state'];
  prNumber?: number;
  prUrl?: string;
}

/** Insert a new dev dispatch (id + timestamps minted here). */
export async function saveDevDispatch(
  input: NewDevDispatchInput,
): Promise<DevDispatch> {
  const ts = nowIso();
  const record: DevDispatch = {
    id: uuid(),
    ticketId: input.ticketId,
    ticketTitle: input.ticketTitle,
    repo: input.repo,
    state: input.state ?? 'proposed',
    prNumber: input.prNumber,
    prUrl: input.prUrl,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('devDispatch', record);
  return record;
}

/** Patch a dev dispatch by id. Re-stamps `updatedAt`. */
export async function updateDevDispatch(
  id: string,
  patch: Partial<Omit<DevDispatch, 'id' | 'createdAt' | 'updatedAt'>>,
): Promise<DevDispatch> {
  return db.update('devDispatch', id, { ...patch, updatedAt: nowIso() });
}

export async function removeDevDispatch(id: string): Promise<void> {
  await db.remove('devDispatch', id);
}
